'use client';

import { useLocale, useMessages } from 'next-intl';
import { useRouter, usePathname } from './navigation';
import type { Locale } from './routing';
import type { TranslationKeys } from './translations';

export type Direction = 'ltr' | 'rtl';

// Compatibility hook — matches the original useI18n() API so landing
// components don't require any changes.
export function useI18n() {
  const locale = useLocale() as Locale;
  const messages = useMessages();
  const router = useRouter();
  const pathname = usePathname();

  const direction: Direction = locale === 'ar' ? 'rtl' : 'ltr';

  function setLanguage(lang: Locale) {
    router.replace(pathname, { locale: lang });
  }

  function toggleLanguage() {
    setLanguage(locale === 'en' ? 'ar' : 'en');
  }

  return {
    language: locale,
    direction,
    // Messages structure is identical to TranslationKeys (derived from en.json).
    t: messages as unknown as TranslationKeys,
    setLanguage,
    toggleLanguage,
  };
}

// Compatibility hook — matches the original useDirection() API.
export function useDirection() {
  const locale = useLocale();
  const direction: Direction = locale === 'ar' ? 'rtl' : 'ltr';

  return {
    direction,
    isRTL: direction === 'rtl',
    isLTR: direction === 'ltr',
    startEnd: <T,>(start: T, end: T): T => (direction === 'rtl' ? end : start),
  };
}
